/**
 * Helpers for DSD-aware fulfillment-center assignment.
 * Product-level DSD (`dsd`) is separate from ship quantities.
 */

export function getOrderLineProductId(lineItem) {
  return String(
    lineItem?.admin_product_id ||
      lineItem?.product_id ||
      lineItem?.adminProduct?.id ||
      '',
  ).trim()
}

export function isProductDsdEnabled(lineItem, dsdByProductId = {}) {
  const productId = getOrderLineProductId(lineItem)
  if (
    productId &&
    Object.prototype.hasOwnProperty.call(dsdByProductId, productId)
  ) {
    return dsdByProductId[productId] === true
  }
  return lineItem?.dsd === true
}

export function isExternalProduct(lineItem) {
  const source = String(
    lineItem?.product_source || lineItem?.source || '',
  ).toLowerCase()
  return source === 'external'
}

/**
 * Lines / pending adds with ship qty > 0 that are currently DSD-enabled.
 */
export function collectPositiveDsdSelections({
  lines = [],
  qtyByLine = {},
  pendingAdds = [],
  dsdByProductId = {},
}) {
  const lineHits = []
  for (const li of lines) {
    const qty = Number(qtyByLine[li.id]) || 0
    if (qty <= 0) continue
    if (!isProductDsdEnabled(li, dsdByProductId)) continue
    lineHits.push({
      kind: 'line',
      lineItem: li,
      quantity: qty,
      productId: getOrderLineProductId(li),
      label:
        li.product_name ||
        li.sku ||
        getOrderLineProductId(li) ||
        `Line ${li.id}`,
    })
  }

  const pendingHits = []
  for (const add of pendingAdds) {
    const qty = Number(add.quantity) || 0
    if (qty <= 0) continue
    const productId = String(add.admin_product_id || '').trim()
    const enabled =
      productId &&
      Object.prototype.hasOwnProperty.call(dsdByProductId, productId)
        ? dsdByProductId[productId] === true
        : add.dsd === true
    if (!enabled) continue
    pendingHits.push({
      kind: 'pending',
      pendingAdd: add,
      quantity: qty,
      productId,
      label: add.product_name || add.sku || productId || 'Added product',
    })
  }

  return { lineHits, pendingHits, hasDsd: lineHits.length + pendingHits.length > 0 }
}

/** Zero DSD ship quantities; leave non-DSD quantities unchanged. */
export function zeroDsdQuantities({
  lines = [],
  qtyByLine = {},
  pendingAdds = [],
  dsdByProductId = {},
}) {
  const nextQty = { ...qtyByLine }
  for (const li of lines) {
    if (isProductDsdEnabled(li, dsdByProductId)) {
      nextQty[li.id] = '0'
    }
  }
  const nextPending = pendingAdds.map((add) => {
    const productId = String(add.admin_product_id || '').trim()
    const enabled =
      productId &&
      Object.prototype.hasOwnProperty.call(dsdByProductId, productId)
        ? dsdByProductId[productId] === true
        : add.dsd === true
    return enabled ? { ...add, quantity: '0' } : add
  })
  return { qtyByLine: nextQty, pendingAdds: nextPending }
}

/**
 * Build createShipment/dispatch items from DSD dialog selections only.
 * Excludes non-DSD and zero-qty rows.
 */
export function buildDsdShipmentItems({
  lines = [],
  qtyByLine = {},
  pendingAdds = [],
}) {
  const lineItems = lines
    .map((li) => ({
      order_line_item_id: li.id,
      quantity: Number(qtyByLine[li.id]) || 0,
    }))
    .filter((row) => row.quantity > 0)

  const newSkuItems = []
  for (const add of pendingAdds) {
    const qty = Number(add.quantity) || 0
    if (qty <= 0) continue
    newSkuItems.push({
      admin_product_id: add.admin_product_id,
      quantity: qty,
      unit_price: Number(add.unit_price),
      mrp: Number(add.mrp),
    })
  }

  return [...lineItems, ...newSkuItems]
}

export function buildDsdByProductIdFromLines(lines = []) {
  const map = {}
  for (const li of lines) {
    const id = getOrderLineProductId(li)
    if (!id) continue
    map[id] = li?.dsd === true
  }
  return map
}
